/**
 * TeamWeekForecastStrip.tsx
 * The forward-looking TEAM capacity forecast that sits at the top of the
 * Employee Schedule matrix, aligned to the week columns (MIKE-SIMPLIFY.md PR3 /
 * D3). Per week it shows a compact read of the WHOLE team's load — "avg 78% ·
 * 2 over · 1 has room" — with a colour signal so Mike can scan the weeks and
 * instantly see where the team is over-stretched or has slack.
 *
 * Pure presentation: the numbers come from computeTeamWeekForecast (a pure
 * helper) and colours from theme.schedule.alloc* tokens — no hex literals.
 */

import React from 'react';
import { Box, Checkbox, Tooltip, Typography, FormControlLabel } from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import { weekOfMondayLabel } from './employeeScheduleShared';
import {
  isFlaggedWeek,
  teamForecastSummaryLabel,
  teamLoadBandLabel,
  type TeamLoadBand,
  type TeamWeekForecast,
} from '../../utils/teamWeekForecast';

// Slimmed 52 → 30px in the L1 polish round (2026-07-25): one line per week —
// "● 120% · 1 over" — details stay in the tooltip.
export const FORECAST_STRIP_H = 30;

export interface TeamWeekForecastStripProps {
  /** Weeks to render, in column order (already filtered when the toggle is on). */
  weekStarts: string[];
  /** weekStart → forecast (from computeTeamWeekForecast over the whole team). */
  forecastByWeek: Map<string, TeamWeekForecast>;
  /** Column geometry — must match the matrix so the strip aligns to its columns. */
  nameWidth: number;
  weekWidth: number;
  /** "Only crunch/slack weeks" toggle state + setter. */
  onlyFlagged: boolean;
  onToggleOnlyFlagged: (next: boolean) => void;
  /** Whether any week is flagged at all (disables the toggle when nothing to filter). */
  hasFlaggedWeeks: boolean;
}

/** Band → accent colour token (the strip cell tint + dot). */
function bandColor(theme: Theme, band: TeamLoadBand): string | null {
  switch (band) {
    case 'over':  return theme.schedule.allocOver;
    case 'tight': return theme.schedule.allocAt;
    case 'under': return theme.schedule.allocUnder;
    case 'ok':    return theme.schedule.allocHealthy;
    case 'none':  return null;
  }
}

export const TeamWeekForecastStrip: React.FC<TeamWeekForecastStripProps> = ({
  weekStarts, forecastByWeek, nameWidth, weekWidth,
  onlyFlagged, onToggleOnlyFlagged, hasFlaggedWeeks,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'sticky',
        top: 0,
        zIndex: 7,
        height: FORECAST_STRIP_H,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Title + "only crunch/slack weeks" toggle — single line (sticky left). */}
      <Box
        sx={{
          width: nameWidth, flexShrink: 0, position: 'sticky', left: 0, zIndex: 8,
          backgroundColor: theme.palette.background.paper, px: 1.5,
          display: 'flex', alignItems: 'center', gap: 0.75,
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>Team load / week</Typography>
        <Tooltip title="Show only weeks the team is over, near capacity, or well under (slack to redeploy)." placement="top" arrow>
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.58rem', color: 'text.secondary', whiteSpace: 'nowrap' } }}
            control={
              <Checkbox
                size="small" sx={{ p: 0.25, mr: 0.25, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                checked={onlyFlagged} disabled={!hasFlaggedWeeks}
                onChange={(e) => onToggleOnlyFlagged(e.target.checked)}
              />
            }
            label="only flagged"
          />
        </Tooltip>
      </Box>

      {/* Per-week team read, aligned to the matrix columns. */}
      {weekStarts.map((ws) => {
        const f = forecastByWeek.get(ws);
        const band: TeamLoadBand = f?.band ?? 'none';
        const color = bandColor(theme, band);
        const flagged = isFlaggedWeek(band);
        const tint = color && flagged ? alpha(color, 0.14) : 'transparent';
        const summary = f ? teamForecastSummaryLabel(f) : 'no data';
        return (
          <Tooltip
            key={ws}
            arrow placement="top"
            title={`Week of ${weekOfMondayLabel(ws)} — ${summary}${f && f.scheduledCount > 0 ? ` (${teamLoadBandLabel(band)}, ${f.scheduledCount} scheduled)` : ''}`}
          >
            <Box
              sx={{
                width: weekWidth, flexShrink: 0, borderLeft: `1px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.5, px: 0.5, backgroundColor: tint,
                borderBottom: color && flagged ? `2px solid ${color}` : '2px solid transparent',
              }}
            >
              {f && f.scheduledCount > 0 ? (
                <>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, backgroundColor: color ?? theme.schedule.noActivity }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, lineHeight: 1, color: color ?? 'text.primary' }}>
                    {f.avgPercent}%
                  </Typography>
                  {f.overCount > 0 && (
                    <Typography component="span" sx={{ fontSize: '0.56rem', fontWeight: 700, lineHeight: 1, color: theme.schedule.allocOver, whiteSpace: 'nowrap' }}>
                      {f.overCount} over
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>—</Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};
